/**
 * Pure lookup/transform functions for resolving booking selections
 * (scenes, locations, time slots) into display-friendly objects.
 */
import type { ResolvedScene } from '@/components/CheckoutSummary/types'
import type { SceneGroup } from '@/components/SceneSelection/types'
import { SceneAvailability } from '@/components/SceneSelection/types'

import type { SceneCatalogEntry } from '@VelocitySquared/sunny16-content-dataobjects-cady/booking'

/**
 * Resolves selected scene IDs into display objects with names and status labels.
 * Cross-references the bundle's sceneSelectionContent (for availability)
 * with the master sceneCatalog (for display names).
 *
 * For bundles without sceneSelectionContent, all selected scenes show as "Selected".
 */
export function resolveSelectedScenes(
  sceneGroups: SceneGroup[] | undefined,
  selectedSceneIds: string[],
  sceneCatalog: SceneCatalogEntry[]
): ResolvedScene[] {
  const nameMap = new Map<string, string>()
  for (const scene of sceneCatalog) {
    nameMap.set(scene.identifier, scene.name)
  }
  // No scene selection content — just show all selected as "Selected"
  if (!sceneGroups) {
    return selectedSceneIds.map((sceneId) => ({
      id: sceneId,
      name: nameMap.get(sceneId) ?? sceneId,
      statusLabel: 'Selected'
    }))
  }
  // Build a map of sceneId → availability from all groups
  const availabilityMap = new Map<string, string>()
  for (const group of sceneGroups) {
    for (const item of group.items) {
      availabilityMap.set(item.sceneId, item.availability)
    }
  }
  // Merge included scenes (auto-part of the session) with user-selected scenes
  const allRelevantIds = new Set<string>(selectedSceneIds)
  for (const [sceneId, availability] of availabilityMap) {
    if (availability === SceneAvailability.Included) {
      allRelevantIds.add(sceneId)
    }
  }
  return Array.from(allRelevantIds).map((sceneId) => ({
    id: sceneId,
    name: nameMap.get(sceneId) ?? sceneId,
    statusLabel:
      availabilityMap.get(sceneId) === SceneAvailability.Included ? 'Included' : 'Selected'
  }))
}
